/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { languageCodeToName, toLanguageCode } from 'js/lib/language';

import { Link, PageNotification, breakpoints } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useLanguageInfoDialog } from 'bundles/unified-description-page-common/contexts/LanguageInfoDialogContext';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';

import _t from 'i18n!nls/xdp';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

type Props = {
  productType: string;
  translatedLanguages: Array<{ code: string }>;
  originalLanguage: string;
};

const styles = {
  notificationBanner: css`
    background: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / 25%);
    left: 50%;
    transform: translate(-50%, 0%);
    width: 985px;
    z-index: 5000;
    position: absolute;

    ${breakpoints.down('md')} {
      width: 95%;
    }
  `,
};

const TranslationsNotificationBanner = ({ productType, translatedLanguages, originalLanguage }: Props) => {
  const [hideBanner, setHideBanner] = useState(false);
  const { subfolderLocaleCode } = usePageInfo();
  const { openDialog } = useLanguageInfoDialog();
  const translatedLanguagesCode = translatedLanguages.map((language) => language.code);

  const shouldNotShowBanner =
    hideBanner ||
    !subfolderLocaleCode ||
    // If the original language is the same as the subfolder language, don't show the banner
    toLanguageCode(subfolderLocaleCode) === originalLanguage ||
    // First, find region and language exact match with the subtag (ex: fr-FR)
    translatedLanguagesCode.includes(subfolderLocaleCode) ||
    // If no exact match found then, look for a match without the region (ex: fr)
    translatedLanguagesCode.includes(toLanguageCode(subfolderLocaleCode));

  if (shouldNotShowBanner) {
    return null;
  }

  return (
    <TrackedDiv
      trackingName="xdp_translations_notification_banner"
      withVisibilityTracking
      data-testId="translations-notification-banner"
    >
      <PageNotification
        severity="information"
        title={_t('This #{productType} is not available in #{subfolderLanguage}', {
          productType: productType.toLocaleLowerCase(),
          subfolderLanguage: languageCodeToName(subfolderLocaleCode),
        })}
        onDismiss={() => setHideBanner(true)}
        css={styles.notificationBanner}
      >
        <FormattedMessage
          message={_t("We're translating it into more languages. {viewAllLanguagesLink}")}
          viewAllLanguagesLink={
            translatedLanguages.length > 0 ? (
              <TrackedLink
                trackingName="xdp_translations_notification_banner_view_all_languages"
                onClick={(e) => {
                  e.preventDefault();
                  openDialog();
                }}
                href="#"
              >
                {_t('View the languages we have available.')}
              </TrackedLink>
            ) : null
          }
        />
      </PageNotification>
    </TrackedDiv>
  );
};

export default TranslationsNotificationBanner;
